/**
 * 아이콘 관리 — 콘솔 목록 공통 규격 채택본 (2026-09-09 사용자 지시).
 *
 * 분류축이 없으므로 **형 A**(본문 한 판)다. 나머지는 메뉴 관리와 한 글자도 다르지 않다.
 *
 * ★★ 갈아타면서 **없던 기능이 하나 생긴다 — 「수정」.**
 *    서버에는 `PATCH /api/admin/icons/:id`(label · svg)가 **처음부터 있었는데 화면이
 *    부르지 않아**, 이름표 한 글자를 고치려면 지우고 다시 등록해야 했다. 정본 P3-1 이
 *    「등록만 있고 수정이 없는 마스터 화면은 미완성이다」라고 적어 둔 바로 그 모양이다.
 *    → 서버·마이그레이션 변경 0 으로 채운다(`updateIcon` 은 api 클라이언트에도 이미 있었다).
 *
 * ⚠ 이름(키 `name`)은 못 고친다 — 서버 PATCH 가 label·svg 만 받는다. 메뉴가 그 키로
 *   아이콘을 가리키므로 키를 바꾸면 가리키던 메뉴가 끊긴다. 팝업에서 잠그고 이유를 적는다.
 *
 * ⚠ 이전 화면 `IconRegistrySection`(카드 격자)은 되돌리기 대비로 남겨 뒀고,
 *   등록 폼(`IconModal`)·미리보기(`SvgPreview`)는 그 파일 것을 그대로 쓴다.
 */
import { useEffect, useMemo, useState } from 'react';
import { Pencil, Trash2, X } from '../../../icons';
import { deleteIcon, updateIcon, type IconRow } from '../../../api/icons';
import { useCustomIcons, refreshCustomIcons } from './iconStore';
import { IconModal, SvgPreview } from './IconRegistrySection';
import { ConsoleListScreen } from '../../../components/console-list/ConsoleListScreen';
import { ConsoleListTable } from '../../../components/console-list/ConsoleListTable';

export function IconListSection() {
  const { icons: list } = useCustomIcons();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<IconRow | null>(null);
  const [q, setQ] = useState('');
  const [source, setSource] = useState<'all' | 'ai' | 'manual'>('all');

  useEffect(() => { void refreshCustomIcons().finally(() => setLoading(false)); }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((ic) => {
      if (source !== 'all' && (ic.source === 'ai' ? 'ai' : 'manual') !== source) return false;
      if (!needle) return true;
      return `${ic.label} ${ic.name}`.toLowerCase().includes(needle);
    });
  }, [list, q, source]);

  async function remove(ic: IconRow) {
    if (!window.confirm(`아이콘 "${ic.label}"(${ic.name})을 삭제할까요? 이 아이콘을 쓰는 메뉴는 기본 아이콘으로 표시됩니다.`)) return;
    setBusyId(ic.id);
    try { await deleteIcon(ic.id); await refreshCustomIcons(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '삭제 실패'); }
    finally { setBusyId(null); }
  }

  return (
    <>
      <ConsoleListScreen
        title="아이콘 관리"
        kpi={`커스텀 ${list.length}`}
        desc="커스텀 SVG 아이콘(명칭·이미지·저장위치) · AI 생성 — 메뉴 관리의 아이콘 피커에서 고를 수 있습니다."
        search={{ value: q, onChange: setQ, placeholder: '아이콘 이름 · 키로 찾기' }}
        filters={{
          active: source,
          onChange: (k) => setSource(k as 'all' | 'ai' | 'manual'),
          items: [
            { key: 'all', label: '전체', count: list.length },
            { key: 'ai', label: 'AI 생성', count: list.filter((i) => i.source === 'ai').length },
            { key: 'manual', label: '직접 등록', count: list.filter((i) => i.source !== 'ai').length },
          ],
        }}
        add={{ label: '아이콘 등록', onClick: () => setAdding(true) }}
        notice={error ? <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div> : null}
      >
        {loading ? (
          <div className="cl-empty">불러오는 중…</div>
        ) : (
          <ConsoleListTable
            rows={shown}
            rowKey={(ic) => ic.id}
            onRowClick={(ic) => setEditing(ic)}
            empty={list.length === 0
              ? <>등록된 커스텀 아이콘이 없습니다.<span className="cl-empty__sub">위의 「아이콘 등록」으로 만듭니다.</span></>
              : <>찾는 아이콘이 없습니다.<span className="cl-empty__sub">검색어나 필터를 지워 보세요.</span></>}
            columns={[
              {
                key: 'icon', header: '아이콘',
                render: (ic) => (
                  <span className="cl-name">
                    <span className="cl-name__ico"><SvgPreview svg={ic.svg} size={20} /></span>
                    <span className="cl-name__t">{ic.label}</span>
                    <span className="cl-name__s">custom:{ic.name}</span>
                  </span>
                ),
              },
              {
                key: 'source', header: '출처',
                render: (ic) => <span className="cl-badge">{ic.source === 'ai' ? 'AI 생성' : '직접 등록'}</span>,
              },
            ]}
            rowActions={(ic) => (
              <>
                <button
                  type="button" className="cl-rowbtn"
                  aria-label={`${ic.label} 수정`} title="수정" onClick={() => setEditing(ic)}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button" className="cl-rowbtn cl-rowbtn--danger" disabled={busyId === ic.id}
                  aria-label={`${ic.label} 삭제`} title="삭제" onClick={() => void remove(ic)}
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          />
        )}
      </ConsoleListScreen>

      {adding && (
        <IconModal
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); void refreshCustomIcons(); }}
        />
      )}
      {editing && (
        <IconEditModal
          icon={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void refreshCustomIcons(); }}
        />
      )}
    </>
  );
}

/** 수정 팝업 — 서버가 받는 것(label · svg)만 연다. 키는 잠그고 왜 잠갔는지 적는다. */
function IconEditModal({ icon, onClose, onSaved }: {
  icon: IconRow; onClose: () => void; onSaved: () => void;
}) {
  const [label, setLabel] = useState(icon.label);
  const [svg, setSvg] = useState(icon.svg);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  const validSvg = /<svg[\s\S]*<\/svg>/i.test(svg);

  async function save() {
    setSaving(true); setErr(null);
    try { await updateIcon(icon.id, { label: label.trim() || icon.name, svg }); onSaved(); }
    catch (e: any) { setErr(e?.response?.data?.error ?? e?.message ?? '저장 실패'); }
    finally { setSaving(false); }
  }

  return (
    <div className="svc-modal__backdrop" role="presentation" onClick={onClose}>
      <div className="svc-modal" role="dialog" aria-modal="true" aria-label="아이콘 수정" onClick={(e) => e.stopPropagation()}>
        <div className="svc-modal__head">
          <h3>아이콘 수정</h3>
          <button type="button" className="svc-modal__close" onClick={onClose} aria-label="닫기"><X size={16} /></button>
        </div>
        <div className="svc-modal__body">
          <div className="svc-iconprev">
            <SvgPreview svg={svg} size={40} />
            <div className="svc-iconprev__meta">
              <span>{validSvg ? '미리보기' : 'SVG 를 읽을 수 없습니다'}</span>
              <span className="svc-card__chip">custom:{icon.name}</span>
            </div>
          </div>
          <div className="svc-field">
            {/* ⚠ 이 파일의 새 폼은 label 을 «붙여서» 쓴다 — 옛 svc-field 는 붙이지 않아
                 읽어 주는 프로그램이 어느 칸인지 말해 주지 못한다. */}
            <label htmlFor="icon-edit-key">이름(키)</label>
            <input id="icon-edit-key" value={icon.name} disabled readOnly />
            <span className="svc-field__hint">키는 바꿀 수 없습니다 — 이 키로 아이콘을 가리키는 메뉴가 끊깁니다.</span>
          </div>
          <div className="svc-field">
            <label htmlFor="icon-edit-label">표시 명칭</label>
            <input id="icon-edit-label" value={label} onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="svc-field">
            <label htmlFor="icon-edit-svg">SVG 본문</label>
            <textarea
              id="icon-edit-svg" rows={6} value={svg} spellCheck={false}
              style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.74rem', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', resize: 'vertical' }}
              onChange={(e) => setSvg(e.target.value)}
            />
            <span className="svc-field__hint">저장 시 script/이벤트핸들러는 자동 제거됩니다.</span>
          </div>
          {err && <div className="svc-test svc-test--bad" role="alert"><span className="svc-dot svc-dot--bad" />{err}</div>}
        </div>
        <div className="svc-modal__foot">
          <span className="spacer" />
          <button type="button" className="svc-mbtn" onClick={onClose}>취소</button>
          <button type="button" className="svc-mbtn svc-mbtn--primary" disabled={!validSvg || saving} onClick={() => void save()}>
            {saving ? '저장 중…' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
